import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { EmailTemplate } from '@rossum/api-client/emailTemplates';
import { ChoiceDialog } from '@rossum/components/choice-dialog/ChoiceDialog';
import { Alert, Stack } from '@rossum/ui/material';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelectList } from '../../queues/select-queue/QueueSelectList';

type AddQueuesDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (queueUrls: Url[]) => void;
  isLoading: boolean;
  existingQueueUrls: Url[];
  emailTemplates?: Pick<EmailTemplate, 'name'>[];
};

export const AddQueuesDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  existingQueueUrls,
  emailTemplates = [],
}: AddQueuesDialogProps) => {
  const intl = useIntl();
  const [selectedQueues, setSelectedQueues] = useState<number[]>([]);
  const hasEmailTemplate = emailTemplates.length > 0;
  const { workspacesWithQueues, isLoading: isFetchingQueues } =
    useWorkspacesWithQueues({
      enableQueries: open,
    });

  const filteredQueues = useMemo(() => {
    const existingQueueUrlSet = new Set(existingQueueUrls);
    return (
      workspacesWithQueues?.map(ws => {
        const availableQueues = ws.queues.filter(
          q => !existingQueueUrlSet.has(q.url)
        );
        return { ...ws, queues: availableQueues };
      }) ?? []
    );
  }, [workspacesWithQueues, existingQueueUrls]);

  const queueUrlMap = useMemo<Map<number, string>>(() => {
    const allQueues = filteredQueues.flatMap(ws => ws.queues);
    return new Map(allQueues.map(q => [q.id, q.url]));
  }, [filteredQueues]);

  const handleClose = () => {
    setSelectedQueues([]);
    onClose();
  };

  const handleConfirm = () => {
    const queueUrls = selectedQueues.flatMap(id => queueUrlMap.get(id) || []);
    onConfirm(queueUrls);
    handleClose();
  };

  return (
    <ChoiceDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      submitText={intl.formatMessage(
        { id: 'features.ruleManager.queues.addDialog.submit' },
        { count: selectedQueues.length }
      )}
      cancelText={intl.formatMessage({
        id: 'features.ruleManager.queues.addDialog.cancel',
      })}
      title={intl.formatMessage({
        id: 'features.ruleManager.queues.addDialog.title',
      })}
      isLoading={isLoading}
      isDisabled={selectedQueues.length === 0}
    >
      <Stack spacing={2}>
        {hasEmailTemplate && (
          <Alert severity="warning">
            {intl.formatMessage(
              {
                id: 'features.ruleManager.queues.addDialog.emailTemplateWarning',
              },
              {
                count: emailTemplates.length,
                templates: emailTemplates.map(t => `"${t.name}"`).join(', '),
              }
            )}
          </Alert>
        )}
        <QueueSelectList
          queuesByWorkspace={filteredQueues}
          isLoading={isFetchingQueues}
          selectedQueues={selectedQueues}
          onSelect={ids => setSelectedQueues(ids)}
          disabled={isLoading}
          autoFocus
        />
      </Stack>
    </ChoiceDialog>
  );
};
